import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { api, connectAuth } from '@/shared/api'

import type { ApiAuthResponse, LoginCredentials, Role, User } from '../types'

export function parseAuthSession(response: ApiAuthResponse): { user: User; token: string } {
  const roles = response.user.roles
  if (
    roles.length !== 1 ||
    !(['admin', 'seller_agent', 'accountant'] as const).includes(roles[0] as Role)
  ) {
    throw new Error('La sesión no contiene exactamente un rol canónico.')
  }
  return {
    user: {
      ...response.user,
      role: roles[0] as Role,
      mustChangePassword: response.user.mustChangePassword ?? false,
    },
    token: response.accessToken,
  }
}

/**
 * =============================================================================
 * ESTADO DE SESIÓN
 * =============================================================================
 * Este es CLIENT STATE: información que vive en el navegador, no en el backend.
 * Por eso usa Zustand y no TanStack Query. La distinción está explicada en
 * docs/03-capa-de-datos.md, y es la decisión que más se equivoca al empezar.
 *
 * Regla rápida:
 *   ¿El dato es del servidor y puede quedar desactualizado?  → TanStack Query
 *   ¿El dato solo existe en esta pestaña del navegador?      → Zustand
 * =============================================================================
 */

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isLoading: boolean

  signIn: (credentials: LoginCredentials) => Promise<User>
  refreshSession: () => Promise<void>
  signOut: () => void
  markRequiredPasswordChanged: () => void
}

export const useAuthStore = create<AuthState>()(
  /**
   * `persist` guarda el estado para que la sesión sobreviva a un F5.
   *
   * ⚠️ DECISIÓN DE SEGURIDAD PENDIENTE
   * Guardar el token en localStorage es cómodo pero vulnerable a XSS: si un
   * atacante logra ejecutar JavaScript en la página (por ejemplo a través de
   * una librería comprometida), puede leer localStorage y robar el token.
   *
   * La alternativa más segura es que el backend envíe el token en una cookie
   * `httpOnly` + `Secure` + `SameSite=Strict`: el JavaScript no puede leerla,
   * el navegador la manda sola, y el frontend deja de tocar el token.
   *
   * Cuál usar depende de cómo lo implemente el backend. Cuando se sepa, se
   * decide y se ajusta AQUÍ y en `withCredentials` del httpClient.
   * Ver docs/04-permisos.md, sección "Dónde vive el token".
   */
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      isLoading: false,

      signIn: async (credentials) => {
        set({ isLoading: true })

        try {
          const response = await api.post<ApiAuthResponse>('/auth/login', credentials)
          const session = parseAuthSession(response)

          set({
            user: session.user,
            token: session.token,
            refreshToken: response.refreshToken,
            isLoading: false,
          })

          return session.user
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      refreshSession: async () => {
        const refreshToken = useAuthStore.getState().refreshToken
        if (!refreshToken) {
          set({ user: null, token: null, refreshToken: null })
          throw new Error('No hay una sesión para actualizar.')
        }

        const response = await api.post<ApiAuthResponse>('/auth/refresh', { refreshToken })
        const session = parseAuthSession(response)
        set({ user: session.user, token: session.token, refreshToken: response.refreshToken })
      },

      signOut: () => {
        set({ user: null, token: null, refreshToken: null })
      },

      markRequiredPasswordChanged: () => {
        set((state) => ({
          user: state.user ? { ...state.user, mustChangePassword: false } : null,
        }))
      },
    }),
    {
      name: 'alto-palermo-auth',
      // Solo se persiste lo necesario. `isLoading` es un estado momentáneo:
      // guardarlo dejaría la app pensando que está cargando al recargar.
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
      }),
    },
  ),
)

/**
 * Conecta la sesión con el cliente HTTP.
 *
 * Esta es la otra mitad de la inversión de dependencias explicada en
 * `shared/api/authBridge.ts`: la capa `shared` definió el hueco, y aquí la
 * feature `auth` lo rellena. Se ejecuta una sola vez, al cargar este módulo.
 *
 * Resultado: el cliente HTTP puede usar el token sin importar nada de
 * `features/`, respetando la regla de dependencias del proyecto.
 */
connectAuth({
  getToken: () => useAuthStore.getState().token,
  onSessionExpired: () => {
    useAuthStore.getState().signOut()
  },
})
